import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CoinMedia, { SignatureBadge } from '../../../components/CoinMedia/CoinMedia';
import AppDownloadButton from '../../../components/AppDownloadButton/AppDownloadButton';
import { LIST_TYPE_LABELS } from '../../../components/CuratedLists/CuratedListRail';
import { listPublicCollections } from '../../../services/publicCollectionsService';
import { CuratedListsService } from '../../../services/curatedListsService';
import { RankingService } from '../../../services/rankingService';
import { getCuratedListCover } from '../../../utils/curatedListCover';
import DemoQuiz from '../demoQuiz/DemoQuiz';
import darkKnightGold from '../../../assets/coin_images/movies/dark_knight_coins/gold.png';
import {
    COIN_TIERS,
    EXAMPLE_RANKING_ITEMS,
    FEATURED_COLLECTION_COUNT,
    FEATURED_COLLECTION_SLUGS,
    FEATURED_PICK_COUNT,
    FEATURED_PICK_SLUGS,
    FEATURED_RANKING_SLUG,
    HERO_COLLECTION_SLUG,
} from './landingContent';
import { collectionRequirementLine, isFeaturableCollection, pickFeatured, pickHeroCollection } from './landingData';
import styles from './Landing.module.css';

export const SLOGAN = 'You’ve watched it. Now prove it.';
export const SUPPORTING_COPY = 'Discover your next favorite. Rank the ones you love. Test your movie & TV knowledge to earn collectible coins and complete collections.';

/** Singular label for a Pick's type ("Starter Pack"), from the Discover rail's plural labels. */
function pickTypeLabel(type) {
    const plural = LIST_TYPE_LABELS[type];
    if (!plural || type === 'other') return 'Aluvo Pick';
    if (type === 'before_you_watch') return plural;
    return plural.replace(/s$/, '');
}

function usePublicContent() {
    const [collections, setCollections] = useState(null); // null = loading
    const [picks, setPicks] = useState(null);

    useEffect(() => {
        let cancelled = false;
        listPublicCollections()
            .then((data) => { if (!cancelled) setCollections(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setCollections([]); });
        CuratedListsService.list({ limit: 50 })
            .then((data) => { if (!cancelled) setPicks(data?.curatedLists || []); })
            .catch(() => { if (!cancelled) setPicks([]); });
        return () => { cancelled = true; };
    }, []);

    return { collections, picks };
}

function useFeaturedRanking() {
    const [ranking, setRanking] = useState(null);
    useEffect(() => {
        if (!FEATURED_RANKING_SLUG) return undefined;
        // Any failure (404, network) leaves `ranking` null → the example card.
        let cancelled = false;
        RankingService.getShared(FEATURED_RANKING_SLUG)
            .then((data) => {
                // Only a published, public ranking is ever featured.
                if (!cancelled && data && !data.isPrivate && data.ranking && data.owner?.username) setRanking(data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
    return ranking;
}

/* ── 1. Hero ─────────────────────────────────────────────────────────────── */

function HeroCoin({ collection, loading }) {
    if (loading) {
        return <div className={styles.heroCoinPlaceholder} aria-hidden="true" />;
    }

    if (!collection) {
        return (
            <figure className={styles.heroFigure}>
                <img className={styles.heroCoinImage} src={darkKnightGold} alt="The Dark Knight Gold coin: a perfect 8 of 8 on The Dark Knight" />
                <figcaption className={styles.heroCaption}>
                    <span className={styles.rewardLabel}>Title coin · Gold</span>
                    <strong>The Dark Knight</strong>
                    <span>Earned with a perfect 8 of 8.</span>
                </figcaption>
            </figure>
        );
    }

    const rewardName = collection.reward?.name || collection.name;
    return (
        <figure className={styles.heroFigure}>
            <div className={styles.heroCoinFrame}>
                <CoinMedia
                    coin={collection.reward}
                    animate
                    className={styles.heroCoinImage}
                    alt={`${rewardName}, the collection reward coin for completing ${collection.name}`}
                />
            </div>
            <figcaption className={styles.heroCaption}>
                <span className={styles.rewardLabel}>Collection reward{collection.reward?.media?.type === 'ANIMATED' ? ' · Signature coin' : ''}</span>
                <strong>{rewardName}</strong>
                <span>
                    Not a single-title coin: it unlocks when you complete{' '}
                    <a href={`/collections/${collection.slug}`}>{collection.name}</a>. {collectionRequirementLine(collection)}.
                </span>
            </figcaption>
        </figure>
    );
}

function Hero({ heroCollection, loading }) {
    return (
        <section className={`${styles.section} ${styles.hero}`} aria-labelledby="hero-title">
            <div className={styles.heroAura} aria-hidden="true" />
            <div className={styles.heroCopy}>
                <h1 id="hero-title" className={styles.heroTitle}>
                    <span>You’ve watched it.</span> <span>Now prove it.</span>
                </h1>
                <p className={styles.heroSubcopy}>{SUPPORTING_COPY}</p>
                <div className={styles.ctaRow}>
                    <a href="/register" className={styles.primaryCta}>Start Earning Coins</a>
                    <a href="#explore" className={styles.secondaryCta}>Explore Aluvo</a>
                </div>
            </div>
            <HeroCoin collection={heroCollection} loading={loading} />
        </section>
    );
}

/* ── 2. Prove what you watched ───────────────────────────────────────────── */

function ProveSection() {
    return (
        <section id="prove" className={`${styles.section} ${styles.split}`} aria-labelledby="prove-title">
            <div className={styles.sectionCopy}>
                <p className={styles.eyebrow}>Prove what you watched</p>
                <h2 id="prove-title">Pick a title. Answer eight questions.</h2>
                <ol className={styles.steps}>
                    <li><strong>Choose a movie or a TV season</strong> you’ve watched.</li>
                    <li><strong>Answer 8 questions</strong> about what actually happens in it.</li>
                    <li><strong>Get 5 or more right</strong> and the title is verified with a coin on your profile.</li>
                </ol>
                <p className={styles.note}>
                    Try the sample quiz here. It’s a shortened 5-question practice round and doesn’t award a coin.
                </p>
            </div>
            <DemoQuiz />
        </section>
    );
}

/* ── 3. Earn coins and complete collections ──────────────────────────────── */

function TierLadder() {
    return (
        <div className={styles.tierBlock}>
            <ol className={styles.tierGrid} aria-label="Coin tiers">
                {COIN_TIERS.map((tier) => (
                    <li key={tier.key} className={`${styles.tierCard} ${styles[`tier_${tier.key}`]}`}>
                        <img src={tier.image} alt={`The Dark Knight ${tier.label} coin`} loading="lazy" decoding="async" width="160" height="160" />
                        <h3>{tier.label}</h3>
                        <p className={styles.tierScore}>{tier.score}</p>
                        <p>{tier.detail}</p>
                    </li>
                ))}
            </ol>
            <p className={styles.note}>
                Coins only go up: a retry can upgrade a Bronze or Silver coin, never lower it. Below 5 of 8 doesn’t earn a coin.
                Shown: The Dark Knight coin at each tier.
            </p>
        </div>
    );
}

function CollectionCard({ collection }) {
    const rewardName = collection.reward?.name || collection.name;
    const titles = Array.isArray(collection.titles) ? collection.titles : [];
    return (
        <article className={styles.collectionCard}>
            <div className={styles.collectionCoin}>
                <CoinMedia
                    coin={collection.reward}
                    alt={`${rewardName}, the reward coin for ${collection.name}`}
                    imgProps={{ loading: 'lazy', decoding: 'async' }}
                />
                <SignatureBadge coin={collection.reward} compact className={styles.signatureBadge} />
            </div>
            <span className={styles.rewardLabel}>Collection reward</span>
            <h3>{rewardName}</h3>
            <p className={styles.collectionName}>{collection.name}</p>
            <p className={styles.requirement}>{collectionRequirementLine(collection)}. Any tier counts.</p>
            {titles.length > 0 && (
                <ul className={styles.titleList} aria-label={`Titles in ${collection.name}`}>
                    {titles.map((title) => <li key={title}>{title}</li>)}
                </ul>
            )}
            <a className={styles.cardLink} href={`/collections/${collection.slug}`}>
                View collection<span className={styles.srOnly}>: {collection.name}</span>
            </a>
        </article>
    );
}

function ExampleProgress({ collection }) {
    const titles = Array.isArray(collection?.titles) ? collection.titles : [];
    if (titles.length < 3 || collection.completionType === 'MINIMUM_COUNT') return null;
    const verified = Math.max(1, titles.length - 2);
    return (
        <aside className={styles.exampleProgress} aria-labelledby="example-progress-title">
            <p id="example-progress-title" className={styles.exampleLabel}>Example progress</p>
            <h3>{collection.name}</h3>
            <p className={styles.note}>An illustration of how a collection tracks, not anyone’s real account.</p>
            <div className={styles.progressTrack} aria-hidden="true">
                <span style={{ width: `${(verified / titles.length) * 100}%` }} />
            </div>
            <p className={styles.progressText}>{verified} / {titles.length} verified</p>
            <ul className={styles.checklist}>
                {titles.map((title, i) => (
                    <li key={title} className={i < verified ? styles.checked : ''}>
                        <span aria-hidden="true">{i < verified ? '✓' : '○'}</span>
                        {title}<span className={styles.srOnly}>{i < verified ? ' (verified in this example)' : ' (not yet verified in this example)'}</span>
                    </li>
                ))}
            </ul>
        </aside>
    );
}

function CollectSection({ collections, heroCollection, loading }) {
    const featured = pickFeatured(collections, FEATURED_COLLECTION_SLUGS, {
        count: FEATURED_COLLECTION_COUNT,
        allow: isFeaturableCollection,
        exclude: heroCollection ? [heroCollection.slug] : [],
    });

    return (
        <section id="collect" className={styles.section} aria-labelledby="collect-title">
            <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Earn coins and complete collections</p>
                <h2 id="collect-title">Your score sets the coin.</h2>
                <p>Every movie and TV season has its own coin. How many of the 8 questions you get right decides the tier.</p>
            </div>

            <TierLadder />

            <div className={`${styles.sectionHeader} ${styles.subHeader}`}>
                <h2>Collections worth completing.</h2>
                <p>
                    A collection groups titles around a director, a franchise or a theme. Verify the titles it asks for, at any
                    tier, and you unlock its reward: a separate coin you can’t get any other way.
                </p>
            </div>

            {loading && <div className={styles.collectionGrid} aria-hidden="true">{[0, 1, 2, 3].map((i) => <div key={i} className={styles.cardSkeleton} />)}</div>}
            {!loading && featured.length > 0 && (
                <div className={styles.collectionGrid}>
                    {featured.map((c) => <CollectionCard key={c.slug} collection={c} />)}
                </div>
            )}
            <div className={styles.collectFooter}>
                {!loading && <ExampleProgress collection={heroCollection} />}
                <a href="/collections" className={styles.secondaryCta}>Browse all collections</a>
            </div>
        </section>
    );
}

/* ── 4. Show your taste ──────────────────────────────────────────────────── */

export function RankingVisual() {
    const featured = useFeaturedRanking();
    // The JSON said public, but the card can still 404 if it was unpublished since.
    const [cardFailed, setCardFailed] = useState(false);

    if (featured && !cardFailed) {
        const { ranking, owner } = featured;
        const slug = featured.slug || FEATURED_RANKING_SLUG;
        const username = owner?.username;
        return (
            <figure className={styles.rankingFigure} aria-labelledby="featured-ranking-label">
                <span id="featured-ranking-label" className={styles.rewardLabel}>A ranking made on Aluvo</span>
                <a href={`/rankings/${slug}`} className={styles.rankingCardLink}>
                    <img
                        src={RankingService.sharedCardUrl(slug, { format: 'square' })}
                        alt={`Share card for “${ranking.title}”, a Top ${ranking.size} by Aluvo member @${username}`}
                        loading="lazy"
                        decoding="async"
                        width="1080"
                        height="1080"
                        onError={() => setCardFailed(true)}
                    />
                </a>
                <figcaption>
                    <a href={`/rankings/${slug}`}><strong>{ranking.title}</strong></a>
                    <span>A Top {ranking.size} by Aluvo member <strong>@{username}</strong></span>
                </figcaption>
            </figure>
        );
    }

    return (
        <figure className={styles.rankingFigure}>
            <img
                src={RankingService.previewCardUrl({ items: EXAMPLE_RANKING_ITEMS, size: 5, format: 'square' })}
                alt="Example ranking share card: a Top 5 of The Dark Knight, Interstellar, Pulp Fiction, The Matrix and Inception"
                loading="lazy"
                decoding="async"
                width="1080"
                height="1080"
            />
            <figcaption>Example share image, made with the same ranking tool you’ll use.</figcaption>
        </figure>
    );
}

function RankingsSection() {
    return (
        <section id="rankings" className={`${styles.section} ${styles.split} ${styles.splitReverse}`} aria-labelledby="rankings-title">
            <div className={styles.sectionCopy}>
                <p className={styles.eyebrow}>Show your taste</p>
                <h2 id="rankings-title">Rank the ones you love.</h2>
                <p>
                    Movies, TV series, or both. Anime, comfort rewatches, the best heist films ever made: name it whatever you
                    like and put your favorites in order.
                </p>
                <ul className={styles.featureList}>
                    <li><strong>Top 5 or Top 10</strong>, and you can grow it to a Top 15 or Top 20 later.</li>
                    <li><strong>Share image your way:</strong> Poster Grid or Compact List, square or Story size.</li>
                    <li><strong>Your call what shows:</strong> your @username, the rank numbers, and the titles under each poster.</li>
                    <li><strong>Private until you publish.</strong> Drafts are only visible to you.</li>
                </ul>
                <a href="/rankings/new" className={styles.primaryCta}>Create Your Ranking</a>
            </div>
            <RankingVisual />
        </section>
    );
}

/* ── 5. Find your next watch ─────────────────────────────────────────────── */

function PickCard({ pick }) {
    const cover = getCuratedListCover(pick);
    return (
        <a href={`/discover/lists/${pick.slug}`} className={styles.pickCard}>
            <div className={styles.pickCover}>
                {cover.url
                    ? <img src={cover.url} alt="" loading="lazy" decoding="async" />
                    : <span aria-hidden="true">Aluvo</span>}
            </div>
            <div className={styles.pickCopy}>
                <span className={styles.pickType}>{pickTypeLabel(pick.listType)}</span>
                <h3>{pick.title}</h3>
                {pick.description && <p>{pick.description}</p>}
            </div>
        </a>
    );
}

function PicksSection({ picks, loading }) {
    const featured = pickFeatured(picks, FEATURED_PICK_SLUGS, { count: FEATURED_PICK_COUNT });
    // Only name the kinds of Picks that are actually published right now.
    const types = [...new Set((picks || []).map((p) => p.listType).filter((t) => t && LIST_TYPE_LABELS[t] && t !== 'other'))]
        .map((t) => LIST_TYPE_LABELS[t]);

    return (
        <section id="picks" className={styles.section} aria-labelledby="picks-title">
            <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Find your next watch</p>
                <h2 id="picks-title">Aluvo Picks.</h2>
                <p>
                    Hand-picked lists from the Aluvo team, each with a short note on why every title made the cut.
                    {types.length > 0 && <> Right now: {types.join(', ')}.</>}
                </p>
            </div>

            {loading && <div className={styles.pickGrid} aria-hidden="true">{[0, 1, 2, 3].map((i) => <div key={i} className={styles.cardSkeleton} />)}</div>}
            {!loading && featured.length > 0 && (
                <div className={styles.pickGrid}>
                    {featured.map((pick) => <PickCard key={pick.slug} pick={pick} />)}
                </div>
            )}

            <div className={styles.pickNotes}>
                <div>
                    <h3>Save it for later</h3>
                    <p>
                        Add any movie or show to your watchlist. On a Pick, one tap adds every title you haven’t verified yet.
                    </p>
                </div>
                <div>
                    <h3>Picks vs. Collections</h3>
                    <p>
                        Picks point you to something worth watching; they don’t award a coin of their own. Collections award a
                        separate reward coin once you complete them.
                    </p>
                </div>
            </div>

            <a href="/discover?feed=aluvo-picks" className={styles.secondaryCta}>Browse Aluvo Picks</a>
        </section>
    );
}

/* ── 6. Final CTA ────────────────────────────────────────────────────────── */

function FinalCta() {
    return (
        <section className={`${styles.section} ${styles.finalCta}`} aria-labelledby="final-cta-title">
            <div>
                <h2 id="final-cta-title">Start with a movie you know by heart.</h2>
                <p>Take the challenge. Earn your first coin. Build from there.</p>
            </div>
            <div className={styles.finalActions}>
                <a href="/register" className={styles.primaryCta}>Get Started</a>
                <AppDownloadButton source="home_final_cta" />
            </div>
        </section>
    );
}

export default function LandingPage() {
    const { collections, picks } = usePublicContent();
    const collectionsLoading = collections === null;
    const heroCollection = pickHeroCollection(collections, HERO_COLLECTION_SLUG);

    return (
        <main className={styles.page}>
            <Helmet>
                <title>Aluvo | You’ve watched it. Now prove it.</title>
                <meta name="description" content={`${SUPPORTING_COPY} Bronze, Silver, Gold and Legendary coins for the movies and shows you know best.`} />
                <meta property="og:title" content="Aluvo | You’ve watched it. Now prove it." />
                <meta property="og:description" content={SUPPORTING_COPY} />
                <meta name="twitter:title" content="Aluvo | You’ve watched it. Now prove it." />
                <meta name="twitter:description" content={SUPPORTING_COPY} />
            </Helmet>

            <Hero heroCollection={heroCollection} loading={collectionsLoading} />
            <div id="explore" className={styles.exploreAnchor} />
            <ProveSection />
            <CollectSection collections={collections} heroCollection={heroCollection} loading={collectionsLoading} />
            <RankingsSection />
            <PicksSection picks={picks} loading={picks === null} />
            <FinalCta />
        </main>
    );
}
